import React, { useEffect, useState } from 'react';
import { fetchMedicalColleges, createMedicalCollege, updateMedicalCollege, deleteMedicalCollege, filterMedicalColleges } from './api';
import { fetchCandidates, createCandidate, updateCandidate, deleteCandidate } from './candidateApi';
import CustomNavbar from '../CustomPermissions/CustomNavbar';

const MedicalCollegeList = () => {
    const [medicalColleges, setMedicalColleges] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedCollegeId, setExpandedCollegeId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
    const [selectedCollegeCandidates, setSelectedCollegeCandidates] = useState(null);
    const [candidatesLoading, setCandidatesLoading] = useState(false);
    const [allCandidates, setAllCandidates] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [jumpPage, setJumpPage] = useState('');
    // Candidate modal state
    const [isCandidateModalOpen, setIsCandidateModalOpen] = useState(false);
    const [candidateModalMode, setCandidateModalMode] = useState('create');
    const [currentCandidate, setCurrentCandidate] = useState({
        name: '',
        medical_college: '',
        passing_year: '',
        passing_month: '',
        contact_number: '',
        preferred_location: '',
        state: '',
        current_city: '',
        native_city: '',
        speciality: '',
        spouse_speciality: '',
    });
    const [currentCollege, setCurrentCollege] = useState({
        name: '',
        city: '',
        state: '',
        qualification: '',
        specialization: '',
        specialityType: '',
        seats: null,
        passoutmonth: null,
        passoutyear: null,
    });
    const [filters, setFilters] = useState({
        specialization: '',
        qualification: '',
        passoutmonth: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    const isSuperuser = localStorage.getItem("is_superuser") === "true";

    // (Frontend filtering has been moved to backend debounced API calls)

    const handlePageChange = (pageNum) => {
        setPageNumber(pageNum)
        setJumpPage('') // Clear jump input on page change
        console.log("Page Number: ", pageNum)
    }

    const handleJumpSubmit = (e) => {
        e.preventDefault();
        const page = parseInt(jumpPage);
        if (page >= 1 && page <= totalPages) {
            handlePageChange(page);
        } else {
            alert(`Please enter a page number between 1 and ${totalPages}`);
        }
    };

    // Fetch medical colleges and candidates with debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const getData = async () => {
                try {
                    setIsLoading(true);
                    const [collegesResponse, candidatesResponse] = await Promise.all([
                        fetchMedicalColleges(pageNumber, searchTerm, filters),
                        fetchCandidates(pageNumber)
                    ]);
                    setMedicalColleges(collegesResponse.data.results || collegesResponse.data);
                    setTotalCount(collegesResponse.data.count || 0);
                    setTotalPages(collegesResponse.data.total_pages || 1);
                    setAllCandidates(candidatesResponse.data.results || candidatesResponse.data);
                    setError(null);
                } catch (error) {
                    console.error('Error fetching data:', error);
                    setError('Failed to load data. Please try again.');
                } finally {
                    setIsLoading(false);
                }
            };
            getData();
        }, 500); // 500ms debounce
        return () => clearTimeout(timeoutId);
    }, [pageNumber, searchTerm, filters]);

    // Handle delete medical college
    const handleDelete = async (id) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this medical college?');
        if (confirmDelete) {
            try {
                await deleteMedicalCollege(id);
                setMedicalColleges(medicalColleges.filter((c) => c.id !== id));
                setExpandedCollegeId(null);
            } catch (error) {
                console.error('Error deleting medical college:', error);
                alert('Failed to delete medical college. Please try again.');
            }
        }
    };

    // Open modal for creating/editing a medical college
    const openModal = (mode, college = null) => {
        setModalMode(mode);
        setCurrentCollege(mode === 'create'
            ? {
                name: '',
                city: '',
                state: '',
                qualification: '',
                specialization: '',
                specialityType: '',
                seats: null,
                passoutmonth: null,
                passoutyear: null,
            }
            : { ...college }
        );
        setIsModalOpen(true);
    };

    // Handle modal submission
    const handleModalSubmit = async (e) => {
        e.preventDefault();
        try {
            if (modalMode === 'create') {
                const response = await createMedicalCollege(currentCollege);
                setMedicalColleges([...medicalColleges, response.data]);
            } else {
                const response = await updateMedicalCollege(currentCollege.id, currentCollege);
                setMedicalColleges(medicalColleges.map(c =>
                    c.id === currentCollege.id ? response.data : c
                ));
                setExpandedCollegeId(null);
            }
            setIsModalOpen(false);
        } catch (error) {
            console.error(`Error ${modalMode === 'create' ? 'creating' : 'updating'} medical college:`, error);
            if (error.response && error.response.data && error.response.data.non_field_errors) {
                alert(`Error: ${error.response.data.non_field_errors[0]}`);
            } else {
                alert(`Failed to ${modalMode === 'create' ? 'create' : 'update'} medical college. Please try again.`);
            }
        }
    };

    // Handle input changes in the modal
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentCollege(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Toggle expanded row for additional details
    const toggleExpandedRow = (id) => {
        setExpandedCollegeId(expandedCollegeId === id ? null : id);
    };

    // Handle filter changes
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Apply filters
    const handleFilter = () => {
        setPageNumber(1);
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            specialization: '',
            qualification: '',
            passoutmonth: ''
        });
        setSearchTerm('');
        setPageNumber(1);
    };

    // Handle showing candidates for a specific college
    const handleShowCandidates = (collegeId) => {
        if (selectedCollegeCandidates === collegeId) {
            // If already showing this college's candidates, hide them
            setSelectedCollegeCandidates(null);
        } else {
            // Show candidates for this college
            setSelectedCollegeCandidates(collegeId);
        }
    };

    // Get candidates for a specific college
    const getCandidatesForCollege = (collegeId) => {
        return allCandidates.filter(candidate => candidate.medical_college === collegeId);
    };

    // Candidate modal handlers
    const openCandidateModal = (mode, candidate = null, collegeId = null) => {
        setCandidateModalMode(mode);
        if (mode === 'create') {
            const defaultCollege = collegeId ? medicalColleges.find(c => c.id === collegeId) : null;
            setCurrentCandidate({
                name: '',
                medical_college: collegeId || '',
                passing_year: '',
                passing_month: '',
                contact_number: '',
                preferred_location: '',
                state: '',
                current_city: '',
                native_city: '',
                speciality: defaultCollege ? defaultCollege.specialization : '',
                spouse_speciality: '',
            });
        } else {
            setCurrentCandidate({ ...candidate });
        }
        setIsCandidateModalOpen(true);
    };

    const handleCandidateInputChange = (e) => {
        const { name, value } = e.target;

        let newSpeciality = currentCandidate.speciality;
        if (name === 'medical_college') {
            const selectedCollege = medicalColleges.find(c => c.id.toString() === value.toString());
            newSpeciality = selectedCollege ? selectedCollege.specialization : '';
        }

        setCurrentCandidate(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'medical_college' && { speciality: newSpeciality })
        }));
    };

    const handleCandidateSubmit = async (e) => {
        e.preventDefault();
        try {
            if (candidateModalMode === 'create') {
                const response = await createCandidate(currentCandidate);
                setAllCandidates([...allCandidates, response.data]);
            } else {
                const response = await updateCandidate(currentCandidate.id, currentCandidate);
                setAllCandidates(allCandidates.map(c =>
                    c.id === currentCandidate.id ? response.data : c
                ));
            }
            setIsCandidateModalOpen(false);
        } catch (error) {
            console.error(`Error ${candidateModalMode === 'create' ? 'creating' : 'updating'} candidate:`, error);
            alert(`Failed to ${candidateModalMode === 'create' ? 'create' : 'update'} candidate. Please try again.`);
        }
    };

    const handleDeleteCandidate = async (id) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this candidate?');
        if (confirmDelete) {
            try {
                await deleteCandidate(id);
                setAllCandidates(allCandidates.filter(c => c.id !== id));
            } catch (error) {
                console.error('Error deleting candidate:', error);
                alert('Failed to delete candidate. Please try again.');
            }
        }
    };


    // Error state
    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <h3 className="text-red-700 font-semibold mb-2">Error</h3>
                <p className="text-red-600">{error}</p>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'row', minHeight: '100vh' }}>
            <CustomNavbar />
            <div className="container mx-auto px-4 py-4">
                {/* Medical College Modal */}
                {isModalOpen && (
                    <div
                        className="fixed inset-0 flex justify-center items-center z-50"
                        id="marketingmodalopen"
                        style={{ width: "100vw", height: "100vh" }}
                    >
                        {/* <div className="absolute inset-0 bg-gray-800 bg-opacity-60 backdrop-blur-sm"></div> */}
                        <div
                            className="absolute inset-0 bg-gray-800 bg-opacity-60 backdrop-blur-sm"
                            style={{
                                width: "100vw",
                                height: "150%",
                                background: "black",
                                backgroundSize: "200% 200vh",
                                overflowY: "hidden",
                                paddingBottom: "150px",
                            }}
                        ></div>
                        {/* <div className="relative bg-white w-4/5 max-w-4xl rounded-lg shadow-lg" style={{ marginTop: "100px" }}> */}
                        <div
                            className="relative bg-white rounded-lg shadow-lg"
                            style={{
                                // marginTop: "50px",
                                height: "95vh", // Set height to allow scrolling
                                overflowY: "auto", // Enable scrolling inside modal
                                width: "60vw",
                            }}
                        >
                            <div
                                className="overflow-auto "
                                style={{ padding: "100px", paddingTop: "20px" }}
                            >
                                <h2 className="text-center text-blue-700 mb-5">Add File</h2>
                                <form onSubmit={handleModalSubmit} className="space-y-4">
                                    <datalist id="collegeNames">
                                        {[...new Set(medicalColleges.map(c => c.name))].filter(Boolean).map(name => (
                                            <option key={name} value={name} />
                                        ))}
                                    </datalist>
                                    <datalist id="collegeCities">
                                        {[...new Set(medicalColleges.map(c => c.city))].filter(Boolean).map(city => (
                                            <option key={city} value={city} />
                                        ))}
                                    </datalist>
                                    <datalist id="collegeStates">
                                        {[...new Set(medicalColleges.map(c => c.state))].filter(Boolean).map(state => (
                                            <option key={state} value={state} />
                                        ))}
                                    </datalist>
                                    <datalist id="collegeQualifications">
                                        {[...new Set(medicalColleges.map(c => c.qualification))].filter(Boolean).map(qual => (
                                            <option key={qual} value={qual} />
                                        ))}
                                    </datalist>
                                    <datalist id="collegeSpecializations">
                                        {[...new Set(medicalColleges.map(c => c.specialization))].filter(Boolean).map(spec => (
                                            <option key={spec} value={spec} />
                                        ))}
                                    </datalist>

                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                            Name
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={currentCollege.name}
                                            onChange={handleInputChange}
                                            list="collegeNames"
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                                            City
                                        </label>
                                        <input
                                            type="text"
                                            id="city"
                                            name="city"
                                            value={currentCollege.city}
                                            onChange={handleInputChange}
                                            list="collegeCities"
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                                            State
                                        </label>
                                        <input
                                            type="text"
                                            id="state"
                                            name="state"
                                            value={currentCollege.state}
                                            onChange={handleInputChange}
                                            list="collegeStates"
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="qualification" className="block text-sm font-medium text-gray-700 mb-2">
                                            Qualification
                                        </label>
                                        <input
                                            type="text"
                                            id="qualification"
                                            name="qualification"
                                            value={currentCollege.qualification}
                                            onChange={handleInputChange}
                                            list="collegeQualifications"
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="specialization" className="block text-sm font-medium text-gray-700 mb-2">
                                            Specialization
                                        </label>
                                        <input
                                            type="text"
                                            id="specialization"
                                            name="specialization"
                                            value={currentCollege.specialization}
                                            onChange={handleInputChange}
                                            list="collegeSpecializations"
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="specialityType" className="block text-sm font-medium text-gray-700 mb-2">
                                            Speciality Type
                                        </label>
                                        <select
                                            id="specialityType"
                                            name="specialityType"
                                            value={currentCollege.specialityType}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">Select Speciality Type</option>
                                            <option value="Speciality">Speciality</option>
                                            <option value="Super Speciality">Super Speciality</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="seats" className="block text-sm font-medium text-gray-700 mb-2">
                                            Seats
                                        </label>
                                        <input
                                            type="number"
                                            id="seats"
                                            name="seats"
                                            value={currentCollege.seats}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="passoutmonth" className="block text-sm font-medium text-gray-700 mb-2">
                                            Passing Out Month
                                        </label>
                                        <input
                                            type="number"
                                            id="passoutmonth"
                                            name="passoutmonth"
                                            value={currentCollege.passoutmonth}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="passoutyear" className="block text-sm font-medium text-gray-700 mb-2">
                                            Passing Out Year
                                        </label>
                                        <input
                                            type="number"
                                            id="passoutyear"
                                            name="passoutyear"
                                            value={currentCollege.passoutyear}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div className="flex justify-end space-x-4 mt-6">
                                        <button
                                            type="button"
                                            onClick={() => setIsModalOpen(false)}
                                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                        >
                                            {modalMode === 'create' ? 'Create' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </div>

                        </div>
                    </div>
                )}

                {/* Candidate Modal */}
                {isCandidateModalOpen && (
                    <div className="fixed inset-0 flex justify-center items-center z-50" style={{ width: "100vw", height: "100vh" }}>
                        <div className="absolute inset-0 bg-gray-800 bg-opacity-60 backdrop-blur-sm" onClick={() => setIsCandidateModalOpen(false)}></div>
                        <div className="relative bg-white rounded-lg shadow-lg" style={{ height: "95vh", overflowY: "auto", width: "60vw" }}>
                            <div className="overflow-auto" style={{ padding: "40px" }}>
                                <h2 className="text-center text-blue-700 mb-5 text-2xl font-bold">
                                    {candidateModalMode === 'create' ? 'Add Candidate' : 'Edit Candidate'}
                                </h2>
                                <form onSubmit={handleCandidateSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={currentCandidate.name}
                                            onChange={handleCandidateInputChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Medical College *</label>
                                        <select
                                            name="medical_college"
                                            value={currentCandidate.medical_college}
                                            onChange={handleCandidateInputChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value="">Select Medical College</option>
                                            {medicalColleges.map(college => (
                                                <option key={college.id} value={college.id}>
                                                    {college.name} - {college.city}, {college.state}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Passing Year *</label>
                                            <input
                                                type="number"
                                                name="passing_year"
                                                value={currentCandidate.passing_year}
                                                onChange={handleCandidateInputChange}
                                                required
                                                min="1950"
                                                max="2050"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Passing Month *</label>
                                            <select
                                                name="passing_month"
                                                value={currentCandidate.passing_month}
                                                onChange={handleCandidateInputChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="">Select Month</option>
                                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                                                    <option key={month} value={month}>
                                                        {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][month - 1]}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number *</label>
                                        <input
                                            type="text"
                                            name="contact_number"
                                            value={currentCandidate.contact_number}
                                            onChange={handleCandidateInputChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Speciality *</label>
                                        <input
                                            type="text"
                                            name="speciality"
                                            value={currentCandidate.speciality}
                                            onChange={handleCandidateInputChange}
                                            required
                                            readOnly
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-100 cursor-not-allowed"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
                                            <input
                                                type="text"
                                                name="state"
                                                value={currentCandidate.state}
                                                onChange={handleCandidateInputChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Current City *</label>
                                            <input
                                                type="text"
                                                name="current_city"
                                                value={currentCandidate.current_city}
                                                onChange={handleCandidateInputChange}
                                                required
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Native City</label>
                                        <input
                                            type="text"
                                            name="native_city"
                                            value={currentCandidate.native_city}
                                            onChange={handleCandidateInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Location</label>
                                        <input
                                            type="text"
                                            name="preferred_location"
                                            value={currentCandidate.preferred_location}
                                            onChange={handleCandidateInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Spouse Speciality</label>
                                        <input
                                            type="text"
                                            name="spouse_speciality"
                                            value={currentCandidate.spouse_speciality}
                                            onChange={handleCandidateInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div className="flex justify-end space-x-4 mt-6">
                                        <button
                                            type="button"
                                            onClick={() => setIsCandidateModalOpen(false)}
                                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                        >
                                            {candidateModalMode === 'create' ? 'Create' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-3xl font-bold text-gray-800">
                        Medical Colleges <span className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full ml-3">Total Records: {totalCount}</span>
                    </h2>
                    <button
                        onClick={() => openModal('create')}
                        className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded transition duration-300 ease-in-out"
                    >
                        + Create Medical College
                    </button>
                </div>
                <form onSubmit={(e) => e.preventDefault()}>
                    {/* Search Bar */}
                    <div className="bg-white p-4 rounded-lg shadow-md mb-4">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search by college name, city, or state..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <svg
                                className="absolute left-3 top-3 h-5 w-5 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                            </svg>
                        </div>
                    </div>
                    {/* Filter Section */}
                    <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                        <h3 className="text-lg font-semibold mb-3 text-gray-700">Filter Medical Colleges</h3>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label htmlFor="specialization" className="block text-sm font-medium text-gray-700 mb-1">
                                    Specialization
                                </label>
                                <input
                                    type="text"
                                    id="specialization"
                                    name="specialization"
                                    value={filters.specialization}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <label htmlFor="qualification" className="block text-sm font-medium text-gray-700 mb-1">
                                    Qualification
                                </label>
                                <input
                                    type="text"
                                    id="qualification"
                                    name="qualification"
                                    value={filters.qualification}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div>
                                <label htmlFor="passoutmonth" className="block text-sm font-medium text-gray-700 mb-1">
                                    Passing Out Month
                                </label>
                                <select
                                    id="passoutmonth"
                                    name="passoutmonth"
                                    value={filters.passoutmonth}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All Months</option>
                                    {['January', 'February', 'March', 'April', 'May', 'June',
                                        'July', 'August', 'September', 'October', 'November', 'December'
                                    ].map((month, idx) => (
                                        <option key={idx + 1} value={idx + 1}>{month}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex items-end space-x-2">
                                <button
                                    type="button"
                                    onClick={handleFilter}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                >
                                    Apply Filters
                                </button>
                                <button
                                    type="button"
                                    onClick={resetFilters}
                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
                {/* Medical Colleges Table */}
                {isLoading ? (
                    <div className="flex justify-center items-center h-64 bg-white rounded-lg shadow-md">
                        <p className="text-gray-600 text-lg">Loading medical colleges...</p>
                    </div>
                ) : medicalColleges.length === 0 ? (
                    <div className="bg-gray-100 border border-gray-200 p-6 rounded-lg text-center">
                        <p className="text-gray-600">
                            {searchTerm ? `No medical colleges found matching "${searchTerm}"` : 'No medical colleges found. Create your first medical college!'}
                        </p>
                    </div>
                ) : (
                    <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-blue-500 border-b">
                                    <th className="py-3 px-4 text-center text-white font-semibold">Name</th>
                                    <th className="py-3 px-4 text-left text-white font-semibold">City</th>
                                    <th className="py-3 px-4 text-left text-white font-semibold">State</th>
                                    <th className="py-3 px-4 text-center text-white font-semibold">Qualification</th>
                                    <th className="py-3 px-4 text-center text-white font-semibold">Specialization</th>
                                    <th className="py-3 px-4 text-center text-white font-semibold">Seats</th>
                                    <th className="py-3 px-4 text-right text-white font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {medicalColleges.map((college) => (
                                    <React.Fragment key={college.id}>
                                        <tr className="border-b last:border-b-0 hover:bg-gray-50 transition duration-200">
                                            <td className="py-3 px-4 text-center">
                                                <span className={`px-2 py-1 rounded-full font-semibold`}>
                                                    {college.name}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-left">{college.city}</td>
                                            <td className="py-3 px-4 text-left">{college.state}</td>
                                            <td className="py-3 px-4 text-center">{college.qualification}</td>
                                            <td className="py-3 px-4 text-center">{college.specialization}</td>
                                            <td className="py-3 px-4 text-center">{college.seats}</td>
                                            <td className="py-3 px-4 text-right space-x-2">
                                                <button
                                                    onClick={() => handleShowCandidates(college.id)}
                                                    className="text-green-500 hover:text-blue-700 font-medium mr-2"
                                                >
                                                    {selectedCollegeCandidates === college.id ? `Hide Candidates (${getCandidatesForCollege(college.id).length})` : `Candidate List (${getCandidatesForCollege(college.id).length})`}
                                                </button>
                                                <button
                                                    onClick={() => openModal('edit', college)}
                                                    className="text-blue-500 hover:text-blue-700 font-medium mr-2"
                                                >
                                                    Edit
                                                </button>
                                                {isSuperuser && (
                                                    <button
                                                        onClick={() => handleDelete(college.id)}
                                                        className="text-red-500 hover:text-red-700 font-medium"
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        {selectedCollegeCandidates === college.id && (
                                            <tr>
                                                <td colSpan="7" className="p-4 bg-blue-50">
                                                    <div className="space-y-3">
                                                        <div className="flex justify-between items-center mb-3">
                                                            <h4 className="font-bold text-lg text-blue-800">
                                                                Candidates for {college.name}
                                                            </h4>
                                                            <button
                                                                onClick={() => openCandidateModal('create', null, college.id)}
                                                                className="bg-green-500 hover:bg-green-600 text-white font-semibold py-1 px-4 rounded text-sm transition duration-300"
                                                            >
                                                                + Add Candidate
                                                            </button>
                                                        </div>
                                                        {getCandidatesForCollege(college.id).length === 0 ? (
                                                            <p className="text-gray-600">No candidates found for this medical college.</p>
                                                        ) : (
                                                            <div className="bg-white rounded-lg shadow overflow-x-auto">
                                                                <table className="w-full">
                                                                    <thead>
                                                                        <tr className="bg-blue-400 border-b">
                                                                            <th className="py-2 px-3 text-left text-white font-semibold text-sm">Name</th>
                                                                            <th className="py-2 px-3 text-left text-white font-semibold text-sm">Speciality</th>
                                                                            <th className="py-2 px-3 text-left text-white font-semibold text-sm">Contact</th>
                                                                            <th className="py-2 px-3 text-left text-white font-semibold text-sm">Current City</th>
                                                                            <th className="py-2 px-3 text-left text-white font-semibold text-sm">State</th>
                                                                            <th className="py-2 px-3 text-center text-white font-semibold text-sm">Passing Year</th>
                                                                            <th className="py-2 px-3 text-right text-white font-semibold text-sm">Actions</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {getCandidatesForCollege(college.id).map((candidate) => (
                                                                            <tr key={candidate.id} className="border-b last:border-b-0 hover:bg-gray-50">
                                                                                <td className="py-2 px-3 text-sm">{candidate.name}</td>
                                                                                <td className="py-2 px-3 text-sm">{candidate.speciality}</td>
                                                                                <td className="py-2 px-3 text-sm">{candidate.contact_number}</td>
                                                                                <td className="py-2 px-3 text-sm">{candidate.current_city}</td>
                                                                                <td className="py-2 px-3 text-sm">{candidate.state}</td>
                                                                                <td className="py-2 px-3 text-center text-sm">{candidate.passing_year}</td>
                                                                                <td className="py-2 px-3 text-right text-sm space-x-2">
                                                                                    <button
                                                                                        onClick={() => openCandidateModal('edit', candidate)}
                                                                                        className="text-blue-500 hover:text-blue-700 font-medium"
                                                                                    >
                                                                                        Edit
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => handleDeleteCandidate(candidate.id)}
                                                                                        className="text-red-500 hover:text-red-700 font-medium"
                                                                                    >
                                                                                        Delete
                                                                                    </button>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {expandedCollegeId === college.id && (
                                            <tr>
                                                <td colSpan="7" className="p-4 bg-gray-50">
                                                    <div className="space-y-2">
                                                        <div>
                                                            <h4 className="font-semibold text-gray-700">Speciality Type:</h4>
                                                            <p className="text-gray-600">{college.specialityType}</p>
                                                        </div>
                                                        <div>
                                                            <h4 className="font-semibold text-gray-700">Passing Out Month:</h4>
                                                            <p className="text-gray-600">{college.passoutmonth || 'N/A'}</p>
                                                        </div>
                                                        <div>
                                                            <h4 className="font-semibold text-gray-700">Passing Out Year:</h4>
                                                            <p className="text-gray-600">{college.passoutyear || 'N/A'}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                        {/* --- INSERT THIS PAGINATION BLOCK HERE --- */}
                        <div className="flex flex-col md:flex-row justify-between items-center px-6 py-4 bg-gray-50 border-t border-gray-200 gap-4">
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => handlePageChange(Math.max(pageNumber - 1, 1))}
                                    disabled={pageNumber === 1}
                                    className={`px-4 py-2 font-bold text-white rounded transition-colors ${pageNumber === 1
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-blue-500 hover:bg-blue-700'
                                        }`}
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => handlePageChange(Math.min(pageNumber + 1, totalPages))}
                                    disabled={pageNumber >= totalPages}
                                    className={`px-4 py-2 font-bold text-white rounded transition-colors ${pageNumber >= totalPages
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-blue-500 hover:bg-blue-700'
                                        }`}
                                >
                                    Next
                                </button>
                            </div>

                            <div className="flex items-center space-x-4">
                                <span className="text-gray-700 font-semibold">
                                    Page {pageNumber} of {totalPages}
                                </span>
                                <form onSubmit={handleJumpSubmit} className="flex items-center space-x-2">
                                    <label htmlFor="jumpPage" className="text-sm text-gray-600">Jump to:</label>
                                    <input
                                        type="number"
                                        id="jumpPage"
                                        value={jumpPage}
                                        onChange={(e) => setJumpPage(e.target.value)}
                                        min="1"
                                        max={totalPages}
                                        className="w-16 px-2 py-1 border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        placeholder="No."
                                    />
                                    <button
                                        type="submit"
                                        className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors text-sm font-medium border border-gray-300"
                                    >
                                        Go
                                    </button>
                                </form>
                            </div>
                        </div>
                        {/* --- END PAGINATION BLOCK --- */}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MedicalCollegeList;